import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Image, Keyboard, TouchableOpacity } from 'react-native-web';
import { toast } from 'react-hot-toast';
import { useRoute } from '@react-navigation/native';

// Custom Component
import { Button, TextBox } from '../../components';

// APIs
import { getUserData, loginWithEmail } from '../../modules';

// Root Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import RouteKeys from '../../navigation/RouteKeys';

// Configs
import GLOBAL from '../../configs/config_global';
import routeKeys from '../../navigation/RouteKeys';

// Utils
import { showAlert, showAppError } from '../../utils/util_helpers';
import { getGlobalState, setGlobalState, STATE_KEYS } from '../../configs/config_state';
import Feather from 'react-native-vector-icons/dist/Feather';
import { goSwitchAccount, updateAccountList, updateLocationList } from '../../utils/util_main_app';
import { saveUniqueID } from '../../utils/util_authentication';

export function Login() {
    const { email: passedEmail, password: passedPassword, isAddingAccount = false } = useRoute()?.params || {};

    // region Refs
    const txtEmail = useRef(null);
    const txtPassword = useRef(null);
    // endregion

    // region Local State
    const [email, setEmail] = useState(passedEmail || '');
    const [password, setPassword] = useState(passedPassword || '');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setLoading] = useState(false);
    // endregion

    // region Component Mounting
    useEffect(() => {
        if (!!email && !!password) {
            goLogin();
        }
    }, []);
    // endregion

    // region Function - On Close Pressed (for adding account)
    const _onClosePressed = () => {
        RootNavigation.pop();
        setGlobalState(STATE_KEYS.ADDING_NEW_ACCOUNT, false);
    }
    // endregion

    // region Function - Go Login Vendor - Used by scene_login_email
    const finishLogin = (userDataResponse, service = 'email') => {
        const newAccount = {
            token: GLOBAL.DATA.USER_TOKEN,
            service,
            data: userDataResponse,
            isExpired: false
        };

        // Save new unique ID
        saveUniqueID(userDataResponse.id);

        // Check if this is a fresh login or switching account
        if (isAddingAccount) {
            const accountList = getGlobalState(STATE_KEYS.ACCOUNT_LIST)
            const newAccountList = accountList.concat(newAccount);

            // Save new list to main state
            updateAccountList(newAccountList, newAccountList.length - 1);

            // Pop to switch account window
            _onClosePressed();
            goSwitchAccount(newAccount, newAccountList.length - 1, true);

            // Inform the user
            toast('Successfully logged into "' + userDataResponse.name + '", you can now find this account in Switch Account screen');
        }
        else {
            // Update data to main state
            updateLocationList(0, userDataResponse.locations);
            updateAccountList([newAccount], 0);

            // Inform the user
            toast('Welcome back to Truckster Vendor, ' + userDataResponse.name);

            // Move to scene home
            RootNavigation.reset(routeKeys.MAIN);
        }
    }
    // endregion

    // region Function - Go Login
    const goLogin = () => {
        Keyboard.dismiss();
        setLoading(true);
        loginWithEmail(email, password, isAddingAccount)
            .then((result) => finishLogin(result))
            .catch((error) => {
                if (error === 'Account existed') {
                    RootNavigation.pop();
                    showAlert({ text: 'Unable to add this account, this account is already existed in your account list.' });
                }
                else {
                    if (error.includes('Your account is not valid or still inactive') || error.includes('Sorry, we don\'t recognize that email')) {
                        showAlert({
                            text: `Sorry, we don't recognize that email. If you have just setup your truck, it is still under review. Please try again later.`,
                            confirmButton: {
                                text: 'Contact Us',
                                onPress: () => window.open('mailto:vendor.support@gotruckster.com?subject=Help%20Needed&body=Hello,%20I%20need%20help%20with%20my%20Vendor%20account', '_blank'),
                                color: GLOBAL.COLOR.BASIC.UI_BLUE
                            },
                            cancelButton: { text: 'Got it' }
                        });
                    }
                    else showAppError(error);
                }
            })
            .finally(() => setLoading(false));
    }

    const goLoginWithToken = (token) => {
        Keyboard.dismiss();
        setLoading(true);
        getUserData(token, 'token')
            .then((userData) => finishLogin(userData, 'token'))
            .catch((error) => showAppError('Unable to login, please try again later.<br/><br/>Error message: ' + error))
            .finally(() => setLoading(false));
    }
    // endregion

    // region MAIN RENDER
    const logoWidth = 220;
    return(
        <View style={{ width: '100%', flex: 1, alignItems: 'center', padding: 64, backgroundColor: '#FFF' }}>
            {/* Logo */}
            <Image
                source={require('../../images/logo_vendor_landscape.png')}
                style={{ width: logoWidth, height: (34 / 156) * logoWidth, marginTop: 64 }}
            />

            {/* Main Content */}
            <View style={{ width: '50%', flex: 1, alignSelf: 'center', alignItems: 'center', justifyContent: 'center', paddingTop: 128 }}>
                {/* Welcome Text */}
                <TouchableOpacity onLongPress={() => {
                    showAlert({
                        text: 'Enter token to login',
                        showTextInput: true,
                        cancelButton: { text: 'Cancel' },
                        confirmButton: { text: 'OK', onPress: goLoginWithToken }
                    });
                }}>
                    <Text style={{ fontSize: 40, fontWeight: '900', textAlign: 'center', marginBottom: 36 }}>
                        Welcome back!
                    </Text>
                </TouchableOpacity>

                {/* Email Input */}
                <TextBox
                    key={'txtEmail'}
                    ref={txtEmail}
                    title={'Email'}
                    placeholder={'Email'}
                    returnKeyType={'next'}
                    autoCapitalize={'none'}
                    keyboardType={'email-address'}
                    onSubmitEditing={() => txtPassword.current?.focus()}
                    onChangeText={setEmail}
                    value={email}
                    editable={!isLoading}
                />

                {/* Password Input */}
                <TextBox
                    key={'txtPassword'}
                    ref={txtPassword}
                    containerStyle={{ marginTop: 8 }}
                    onSubmitEditing={() => goLogin()}
                    title={'Password'}
                    placeholder={'Password'}
                    returnKeyType={'done'}
                    autoCapitalize={'none'}
                    secureTextEntry={!showPassword}
                    showHintButton={true}
                    hintButtonImage={'remove-red-eye'}
                    hintButtonOpacity={showPassword ? 1 : 0.5}
                    hintButtonColor={GLOBAL.COLOR.TEXT_GRAY}
                    hintButtonOnPress={() => setShowPassword(!showPassword)}
                    onChangeText={setPassword}
                    value={password}
                    editable={!isLoading}
                />

                {/* Login */}
                <Button
                    style={{ marginTop: 12 }}
                    text={'Login'}
                    locked={!email?.trim() || !password?.trim()}
                    isLoading={isLoading}
                    onPress={() => goLogin()}
                />

                {/* Forgot Password Button */}
                <Text onPress={() => RootNavigation.navigate(RouteKeys.FORGOT_PASSWORD, { isAddingAccount })} style={{ fontWeight: '500', color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, marginTop: 36 }}>
                    Forgot your password?
                </Text>

                {/* Blank Space */}
                <View style={{ flex: 1 }} />

                {/* Register Button */}
                {
                    !isAddingAccount &&
                    <Text style={{ fontWeight: '400', color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY }}>
                        New to Truckster Vendor?
                        <Text onPress={() => RootNavigation.navigate(RouteKeys.REGISTER)} style={{ fontWeight: '500', color: GLOBAL.COLOR.MAIN }}>
                            {'  '}Sign up
                        </Text>
                    </Text>
                }
            </View>

            {/* Back Button (for adding account) */}
            {
                isAddingAccount &&
                <TouchableOpacity
                    onPress={_onClosePressed}
                    style={{ position: 'absolute', top: 64, left: 0, zIndex: 1000, flexDirection: 'row', alignItems: 'center', padding: 24 }}>
                    <Feather color={'red'} name={'arrow-left'} size={32} />
                    <Text style={{ color: 'red', fontSize: 18, fontWeight: 'bold', marginLeft: 16 }}>
                        Back to Switch Account
                    </Text>
                </TouchableOpacity>
            }
        </View>
    );
    // endregion
}
